import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOHead from '@/components/SEOHead';
import { BookOpen, Calculator, ChevronRight, Home } from 'lucide-react';

const SITE_URL = 'https://www.practice4sat.com';

function slugify(text) {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}

const mathSections = [
  {
    title: 'Algebra',
    topics: [
      { title: 'Linear Equations & Inequalities', desc: 'Solve quickly using simplification and balancing strategies', link: '/sat-tricks/linear-equations-inequalities' },
      { title: 'Systems of Equations', desc: 'Use substitution, elimination, and back-solving efficiently', link: '/sat-tricks/systems-of-equations' },
      { title: 'Functions', desc: 'Evaluate and interpret functions without unnecessary steps' },
      { title: 'Graph Interpretation', desc: 'Extract key information directly from graphs' },
      { title: 'Absolute Value Problems', desc: 'Break into cases and solve systematically' },
      { title: 'Exponents & Powers', desc: 'Apply exponent rules to simplify instantly' },
      { title: 'Algebraic Expressions', desc: 'Combine and factor to reduce complexity' },
    ]
  },
  {
    title: 'Advanced Math',
    topics: [
      { title: 'Quadratic Equations', desc: 'Solve using factoring, patterns, and shortcuts' },
      { title: 'Polynomial Functions', desc: 'Analyze behavior and key features quickly' },
      { title: 'Factoring Techniques', desc: 'Break expressions down using common patterns' },
      { title: 'Rational Expressions', desc: 'Simplify and solve without messy algebra' },
      { title: 'Radical Expressions', desc: 'Rewrite and simplify roots efficiently' },
      { title: 'Exponential Functions', desc: 'Recognize growth patterns and solve fast' },
      { title: 'Function Transformations', desc: 'Identify shifts and changes instantly' },
    ]
  },
  {
    title: 'Problem Solving & Data Analysis',
    topics: [
      { title: 'Ratios & Proportions', desc: 'Set up and solve relationships efficiently' },
      { title: 'Percent Problems', desc: 'Convert and calculate without confusion' },
      { title: 'Unit Conversions', desc: 'Track units to avoid mistakes' },
      { title: 'Data Interpretation', desc: 'Read and extract answers directly' },
      { title: 'Mean, Median, and Range', desc: 'Use shortcuts to find statistics quickly' },
      { title: 'Probability', desc: 'Apply simple rules to compute outcomes' },
      { title: 'Word Problems', desc: 'Translate words into equations effectively' },
    ]
  },
  {
    title: 'Geometry & Trigonometry',
    topics: [
      { title: 'Triangles', desc: 'Use key properties and shortcuts' },
      { title: 'Circles', desc: 'Apply formulas and relationships efficiently' },
      { title: 'Lines & Angles', desc: 'Recognize patterns to solve quickly' },
      { title: 'Area & Volume', desc: 'Use formulas without unnecessary steps' },
      { title: 'Coordinate Geometry', desc: 'Work with slopes, distance, and midpoints' },
      { title: 'Trigonometry', desc: 'Recognize patterns and apply relationships efficiently' },
    ]
  }
];

const rwSections = [
  {
    title: 'Standard English Conventions',
    topics: [
      { title: 'Subject-Verb Agreement', desc: 'Match verbs correctly using core rules' },
      { title: 'Punctuation', desc: 'Use punctuation to clarify meaning' },
      { title: 'Sentence Boundaries', desc: 'Fix run-ons and fragments quickly' },
      { title: 'Modifiers', desc: 'Place modifiers correctly to avoid confusion' },
      { title: 'Parallel Structure', desc: 'Keep sentence structure consistent' },
    ]
  },
  {
    title: 'Expression of Ideas',
    topics: [
      { title: 'Transitions', desc: 'Choose words that logically connect ideas' },
      { title: 'Logical Flow', desc: 'Improve clarity by organizing ideas correctly' },
      { title: 'Sentence Placement', desc: 'Position sentences for maximum coherence' },
      { title: 'Concision', desc: 'Eliminate unnecessary words' },
      { title: 'Combining Sentences', desc: 'Merge ideas clearly and effectively' },
    ]
  },
  {
    title: 'Information & Ideas',
    topics: [
      { title: 'Main Idea Questions', desc: 'Identify the central message quickly' },
      { title: 'Supporting Evidence', desc: 'Find proof directly from the text' },
      { title: 'Inference Questions', desc: 'Choose answers supported by context' },
      { title: 'Data Interpretation', desc: 'Connect visuals to the passage' },
      { title: 'Textual Evidence', desc: 'Match answers to exact lines in the text' },
    ]
  },
  {
    title: 'Craft & Structure',
    topics: [
      { title: 'Vocabulary in Context', desc: 'Determine meaning based on usage' },
      { title: "Author's Tone", desc: 'Identify attitude through word choice' },
      { title: 'Passage Purpose', desc: 'Understand why the passage was written' },
      { title: 'Text Structure', desc: 'Recognize how ideas are organized' },
      { title: 'Function of a Sentence', desc: 'Determine the role of a specific line' },
    ]
  }
];

function generateStructuredData() {
  const allSections = [
    ...mathSections.map(s => ({ ...s, category: 'Math' })),
    ...rwSections.map(s => ({ ...s, category: 'Reading and Writing' }))
  ];

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      'name': 'SAT Tricks & Strategies',
      'description': 'Master the SAT Math and Reading & Writing sections with proven tricks and strategies. Explore targeted techniques across all subtopics to solve problems faster and avoid common mistakes.',
      'url': `${SITE_URL}/sat-tricks`,
      'inLanguage': 'en-US',
      'isPartOf': {
        '@type': 'WebSite',
        'name': 'Practice4SAT',
        'url': SITE_URL
      },
      'provider': {
        '@type': 'Organization',
        'name': 'Practice4SAT',
        'url': SITE_URL
      },
      'about': [
        { '@type': 'Thing', 'name': 'SAT Mathematics' },
        { '@type': 'Thing', 'name': 'SAT Reading and Writing' }
      ],
      'mainEntity': {
        '@type': 'ItemList',
        'numberOfItems': allSections.length,
        'itemListElement': allSections.map((section, i) => ({
          '@type': 'ListItem',
          'position': i + 1,
          'name': `SAT ${section.category}: ${section.title}`,
          'url': `${SITE_URL}/sat-tricks/${slugify(section.title)}`
        }))
      }
    },
    {
      '@context': 'https://schema.org',
      '@type': 'EducationalOccupationalProgram',
      'name': 'SAT Tricks & Strategies Guide',
      'description': 'A comprehensive collection of SAT preparation strategies covering all Math and Reading & Writing topics tested on the digital SAT.',
      'provider': {
        '@type': 'Organization',
        'name': 'Practice4SAT',
        'url': SITE_URL
      },
      'educationalProgramMode': 'online',
      'timeToComplete': 'PT10H',
      'occupationalCategory': 'SAT Test Preparation',
      'programPrerequisites': {
        '@type': 'EducationalOccupationalCredential',
        'credentialCategory': 'High School Student'
      }
    }
  ];
}

function TopicCard({ title, desc, link }) {
  const content = (
    <li
      data-testid={`topic-card-${slugify(title)}`}
      style={{
        background: '#fff',
        border: '1px solid #e5e7eb',
        borderRadius: '10px',
        padding: '1.25rem',
        listStyle: 'none',
        transition: 'box-shadow 0.2s, transform 0.2s',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        cursor: link ? 'pointer' : 'default',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)';
        e.currentTarget.style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      <h4 style={{ fontWeight: '600', fontSize: '1.05rem', color: link ? '#0030b3' : '#374151', lineHeight: '1.4', fontFamily: "'Arial', sans-serif", margin: 0 }}>
        {title}
      </h4>
      <p style={{ fontSize: '1rem', color: '#666', lineHeight: '1.5', fontFamily: "'Arial', sans-serif", margin: 0 }}>
        {desc}
      </p>
    </li>
  );
  if (link) {
    return <Link to={link} style={{ textDecoration: 'none', color: 'inherit' }}>{content}</Link>;
  }
  return content;
}

function SectionGroup({ title, sections, accentColor, icon: Icon, startIndex }) {
  return (
    <section aria-labelledby={`section-${slugify(title)}`} style={{ marginBottom: '0' }}>
      <div style={{
        padding: '0 2rem',
        maxWidth: '1400px',
        margin: '0 auto',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.6rem',
          marginBottom: '0',
          paddingBottom: '0.75rem',
          paddingTop: '1rem',
          borderBottom: `2px solid ${accentColor}`,
        }}>
          <Icon size={22} style={{ color: accentColor }} aria-hidden="true" />
          <h2 id={`section-${slugify(title)}`} style={{ fontSize: '1.25rem', fontWeight: '700', color: '#0030b3', margin: 0 }}>
            {title}
          </h2>
        </div>
      </div>

      {sections.map((section, idx) => {
        const globalIdx = startIndex + idx;
        const isGray = globalIdx % 2 === 0;
        const sectionId = `subsection-${slugify(section.title)}`;
        return (
          <section key={sectionId} aria-labelledby={sectionId} style={{
            background: isGray ? '#f8f9fa' : '#ffffff',
            padding: '1.75rem 0',
          }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem' }}>
              <h3 id={sectionId} style={{
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#374151',
                margin: '0 0 1rem 0',
                paddingLeft: '0.25rem',
                fontFamily: "'Arial', sans-serif",
              }}>
                {section.title}
              </h3>
              <ul className="topic-grid" role="list" style={{ padding: 0, margin: 0 }}>
                {section.topics.map((topic, tIdx) => (
                  <TopicCard key={tIdx} title={topic.title} desc={topic.desc} link={topic.link} />
                ))}
              </ul>
            </div>
          </section>
        );
      })}
    </section>
  );
}

const structuredDataArray = generateStructuredData();

const seoKeywords = 'SAT tricks, SAT strategies, SAT math tricks, SAT reading tricks, SAT writing strategies, SAT algebra tricks, SAT geometry shortcuts, SAT problem solving strategies, SAT test taking tips, digital SAT strategies, SAT math shortcuts, SAT reading comprehension strategies, SAT grammar rules, SAT vocabulary strategies, SAT time management tips, SAT score improvement strategies, how to improve SAT score, SAT prep strategies, SAT study tips, best SAT strategies';

export default function SATTricks() {
  return (
    <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', flexDirection: 'column' }}>
      <SEOHead
        title="SAT Tricks & Strategies - Math and Reading & Writing"
        description="Master the SAT with proven tricks and strategies for every Math and Reading & Writing topic. Learn targeted techniques for algebra, geometry, grammar, reading comprehension, and more. Solve problems faster and avoid common mistakes on the digital SAT."
        canonical="/sat-tricks"
        keywords={seoKeywords}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'SAT Tricks & Strategies' }
        ]}
      />

      {/* Structured data inline for pre-rendering (JSON-LD is valid anywhere in the document per Google) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredDataArray[0]) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredDataArray[1]) }} />

      <Header variant="public" />

      <main style={{ flex: 1 }}>
        {/* Breadcrumb navigation */}
        <nav aria-label="Breadcrumb" data-testid="breadcrumb-nav" style={{
          padding: '1rem 2rem 0',
          maxWidth: '1400px',
          margin: '0 auto',
        }}>
          <ol style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            fontSize: '0.9rem',
            fontFamily: "'Arial', sans-serif",
          }}>
            <li>
              <Link to="/" style={{ color: '#6b7280', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Home size={14} aria-hidden="true" />
                <span>Home</span>
              </Link>
            </li>
            <li aria-hidden="true" style={{ color: '#9ca3af' }}>
              <ChevronRight size={14} />
            </li>
            <li>
              <span aria-current="page" style={{ color: '#0030b3', fontWeight: '600' }}>
                SAT Tricks & Strategies
              </span>
            </li>
          </ol>
        </nav>

        <article>
          <div style={{ padding: '2rem 2rem 2rem', maxWidth: '1400px', margin: '0 auto' }}>
            <h1 data-testid="sat-tricks-title" style={{ fontSize: '1.5rem', fontWeight: '600', textAlign: 'center', marginTop: 0, marginBottom: '2rem', color: '#0030b3' }}>
              SAT Tricks & Strategies
            </h1>
            <p style={{ color: '#374151', lineHeight: '1.5', marginBottom: '0.5rem', fontFamily: "'Arial', sans-serif" }}>
              Master the SAT Math and Reading & Writing sections with proven tricks and strategies designed to help you solve problems faster and avoid common mistakes. Explore targeted techniques across all subtopics within each section and learn how to approach each type of SAT question efficiently. For every topic, follow a clear, structured approach: understand the strategy behind the problem, see how it works through a fully solved example, identify common mistakes to avoid, and then try a similar question on your own. Reinforce your understanding with key takeaways and learn when to apply each method with confidence. Start here to build strong problem-solving skills, then apply what you learn in real SAT practice tests to improve your score.
            </p>
          </div>

          <SectionGroup title="Math" sections={mathSections} accentColor="#4f46e5" icon={Calculator} startIndex={0} />
          <SectionGroup title="Reading and Writing" sections={rwSections} accentColor="#0891b2" icon={BookOpen} startIndex={mathSections.length} />

          {/* Internal links section for SEO */}
          <aside style={{ maxWidth: '1400px', margin: '2rem auto', padding: '1.5rem 2rem' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#374151', marginBottom: '0.75rem', fontFamily: "'Arial', sans-serif" }}>
              Continue Your SAT Preparation
            </h2>
            <nav aria-label="Related SAT resources" style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
              <Link to="/quick-quiz" data-testid="link-diagnostic-quiz" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '0.95rem', fontFamily: "'Arial', sans-serif" }}>
                Free SAT Diagnostic Quiz
              </Link>
              <a href="/#pricing" data-testid="link-study-plan" style={{ color: '#0030b3', textDecoration: 'underline', fontSize: '0.95rem', fontFamily: "'Arial', sans-serif" }}>
                Get Your Personalized Study Plan
              </a>
            </nav>
          </aside>
        </article>
      </main>

      <Footer />

      <style>{`
        .topic-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
        }
        @media (max-width: 1024px) {
          .topic-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 600px) {
          .topic-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (min-width: 768px) {
          .desktop-nav {
            display: flex !important;
          }
          .mobile-menu-btn {
            display: none !important;
          }
        }
        @media (max-width: 767px) {
          .desktop-nav {
            display: none !important;
          }
        }
        .footer-link:hover {
          color: #91ccff !important;
        }
      `}</style>
    </div>
  );
}
